import { z } from 'zod';

/**
 * Nombres de archivo y carpeta.
 *
 * La lista negra no es cosmética: el nombre viaja a la interfaz, a las
 * cabeceras HTTP de descarga y —si algún día se exporta a un ZIP— al sistema
 * de archivos de quien descargue. Se filtra aquí, en el borde, y nunca se usa
 * para construir rutas en disco (los blobs se guardan por hash).
 */
const PROHIBIDOS = /[\\/:*?"<>|]/;

/** Caracteres de control: invisibles en pantalla, tóxicos en una cabecera HTTP. */
const tieneControl = (v: string) => [...v].some((c) => c.codePointAt(0)! < 0x20 || c.codePointAt(0) === 0x7f);

export const esquemaNombre = z
  .string()
  .trim()
  .min(1, 'El nombre no puede estar vacío')
  .max(200, 'El nombre no puede superar los 200 caracteres')
  .refine((v) => !PROHIBIDOS.test(v), 'El nombre no puede contener \\ / : * ? " < > |')
  .refine((v) => !tieneControl(v), 'El nombre contiene caracteres no imprimibles')
  .refine((v) => v !== '.' && v !== '..', 'Nombre reservado')
  .refine((v) => !v.endsWith('.'), 'El nombre no puede terminar en punto');

export const esquemaLogin = z.object({
  email: z.string().trim().toLowerCase().email('Correo inválido'),
  password: z.string().min(1, 'Ingresa tu contraseña'),
});

export const esquemaRefresh = z.object({
  refreshToken: z.string().min(10, 'Falta el token de sesión'),
});

export const esquemaCrearCarpeta = z.object({
  padreId: z.string().uuid(),
  nombre: esquemaNombre,
});

export const esquemaRenombrar = z.object({ nombre: esquemaNombre });

export const esquemaIds = z.object({
  ids: z.array(z.string().uuid()).min(1, 'No seleccionaste nada').max(500),
});

export const esquemaMover = esquemaIds.extend({
  destinoId: z.string().uuid(),
});

/** Qué hacer cuando ya existe un archivo con el mismo nombre en el destino. */
export const esquemaConflicto = z.enum(['VERSION', 'RENOMBRAR', 'ERROR']);
export type Conflicto = z.infer<typeof esquemaConflicto>;

export const esquemaPermisos = z.object({
  leer: z.boolean(),
  escribir: z.boolean(),
  eliminar: z.boolean(),
  // Opcional por compatibilidad: un cliente que no lo mande no está pidiendo
  // compartir, y `false` es la lectura correcta de ese silencio.
  compartir: z.boolean().default(false),
  gestionar: z.boolean(),
});

export const esquemaEntradaPermiso = z
  .object({
    sujetoTipo: z.enum(['USUARIO', 'GRUPO', 'TODOS']),
    sujetoId: z.string().uuid().nullable().optional(),
    permisos: esquemaPermisos,
  })
  .refine(
    (e) => (e.sujetoTipo === 'TODOS' ? !e.sujetoId : !!e.sujetoId),
    'Falta indicar a quién se le otorga el permiso',
  );

export const esquemaGuardarPermisos = z.object({
  entradas: z.array(esquemaEntradaPermiso).max(200),
});

export const esquemaHerencia = z.object({
  heredar: z.boolean(),
  /**
   * Al romper la herencia, copiar los permisos heredados como entradas
   * propias. Es lo que hace SharePoint y evita el accidente clásico: romper
   * la herencia y dejar la carpeta sin acceso para nadie.
   */
  copiarActuales: z.boolean().default(true),
});

export const esquemaRol = z.enum(['SUPER_ADMIN', 'ADMIN', 'MIEMBRO']);

export const esquemaCrearUsuario = z.object({
  email: z.string().trim().toLowerCase().email('Correo inválido'),
  nombre: z.string().trim().min(2, 'Nombre demasiado corto').max(120),
  password: z.string().min(10, 'La contraseña debe tener al menos 10 caracteres').max(200),
  rol: esquemaRol.default('MIEMBRO'),
  organizacionId: z.string().uuid().optional(),
  grupos: z.array(z.string().uuid()).default([]),
});

export const esquemaEditarUsuario = z.object({
  nombre: z.string().trim().min(2).max(120).optional(),
  rol: esquemaRol.optional(),
  activo: z.boolean().optional(),
  password: z.string().min(10, 'La contraseña debe tener al menos 10 caracteres').max(200).optional(),
  grupos: z.array(z.string().uuid()).optional(),
});

export const esquemaCrearGrupo = z.object({
  nombre: z.string().trim().min(2, 'Nombre demasiado corto').max(80),
  descripcion: z.string().trim().max(300).nullable().optional(),
});

export const esquemaEditarGrupo = esquemaCrearGrupo.partial().extend({
  usuarios: z.array(z.string().uuid()).optional(),
});

export const esquemaBusqueda = z.object({
  q: z.string().trim().min(2, 'Escribe al menos 2 caracteres').max(120),
  raizId: z.string().uuid().optional(),
  limite: z.coerce.number().int().min(1).max(200).default(60),
});
